import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const PayrollAdd = ({ open, onClose, token }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/payrolls", token);

  // Fetch foreign key data: shops, employees, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop"],
    "shop/shop",
    token
  );
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(
    ["employee"],
    "shop/employees",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const shops = shopsData?.results || [];
  const employees = employeesData?.results || [];
  const currencies = currenciesData?.results || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [salary, setSalary] = useState(""); // To hold the employee's salary
  const [paymentDate, setPaymentDate] = useState("");
  const [monthYear, setMonthYear] = useState("");
  const [notes, setNotes] = useState("");

  // Update salary when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      const employee = employees.find(
        (emp) => emp.id === parseInt(selectedEmployee)
      );
      setSalary(employee ? employee.salary : "");
      setAmount(employee ? employee.salary : ""); // Auto-fill amount with salary
    }
  }, [selectedEmployee, employees]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("employee", selectedEmployee);
    formData.append("currency", selectedCurrency);
    formData.append("amount", amount);
    if (monthYear) {
      const [year, month] = monthYear.split("-");
      formData.append("year", year);
      formData.append("month", month);
    }
    formData.append("payment_date", paymentDate);
    formData.append("notes", notes);
    handleAdd(formData);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop("");
      setSelectedEmployee("");
      setSelectedCurrency("");
      setAmount("");
      setSalary("");
      setPaymentDate("");
      setMonthYear("");
      setNotes("");
      onClose();
    }
  }, [resetForm, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("payroll.addPayroll")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            margin="dense"
            label={t("payroll.selectEmployee")}
            fullWidth
            required
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (employees || []).map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name || "--"}
                </MenuItem>
              ))
            )}
          </TextField>

          {selectedEmployee && (
            <TextField
              margin="dense"
              label={t("payroll.employeeSalary")}
              type="text"
              fullWidth
              value={salary || "--"}
              InputProps={{
                readOnly: true,
              }}
            />
          )}

          <TextField
            select
            margin="dense"
            label={t("payroll.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title || "--"}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("payroll.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />

          {salary && amount && (
            <TextField
              margin="dense"
              label={t("payroll.remainingAmount")}
              type="text"
              fullWidth
              value={(parseFloat(salary) - parseFloat(amount)).toFixed(2)}
              InputProps={{
                readOnly: true,
                style: {
                  color: (parseFloat(salary) - parseFloat(amount)) < 0 ? 'red' : 'green'
                }
              }}
            />
          )}

          <TextField
            margin="dense"
            label={t("payroll.monthYear", "Month/Year")}
            type="month"
            fullWidth
            required
            value={monthYear}
            onChange={(e) => setMonthYear(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            margin="dense"
            label={t("payroll.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            margin="dense"
            label={t("payroll.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("payroll.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedEmployee ||
            !selectedCurrency ||
            !amount ||
            !monthYear ||
            !paymentDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("payroll.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayrollAdd;
